import type { AnyRouter } from '@tanstack/react-router';
import { reloadOnMissingChunk } from './chunk-reload';
import { fetchDeployedVersion, ownVersion } from './deployed-version';
import { reloadBetweenScreens } from './reload-between-screens';
import { reloadHold } from './reload-hold';
import { markUpdated } from './update-state';
import { watchVersion } from './version-watch';

/**
 * A tab left open over a deploy moves onto the new Paige (docs/engineering/web.md, "When Paige is
 * updated"): it asks which Paige is deployed on focus and every five minutes, reloads into the next
 * screen once a newer one is, shows the banner while someone stays on one screen, and reloads once
 * when a screen's code has gone with the old Paige. Nothing reloads by itself while the page is
 * held. A page with no version of its own (the dev server) only keeps the missing-chunk reload.
 */
export function watchForUpdates(router: AnyRouter): void {
  const reload = () => window.location.reload();
  const own = ownVersion();
  const checkNow = async () => {
    const deployed = await fetchDeployedVersion();
    if (own !== null && deployed !== null && deployed !== own) markUpdated(deployed);
  };
  reloadOnMissingChunk({
    held: reloadHold,
    reload,
    navigating: () => router.state.status === 'pending',
    confirmUpdate: () => void checkNow(),
  });
  if (own === null) return;
  watchVersion({ own, fetchDeployed: () => fetchDeployedVersion(), onUpdated: markUpdated });
  reloadBetweenScreens(router, {
    held: reloadHold,
    load: (href) => window.location.assign(href),
  });
}
